import { useAPI } from '@providers/useAPI';
import { keepPreviousData, type QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminToggleAdminRoleResponseDto, AdminUsersResponseDto } from 'app/API';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { adminUsersKeys, TAdminUsersListParams } from 'app/queries/adminUsers/adminUsersKeys';
import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { fetchAllAdminUsersForExport } from '../api/fetchAllAdminUsersForExport';
import { createFetchAdminUsersQueryFn } from '../api/fetchAdminUsers';
import { AdminRemovalError, removeAdminAccess } from '../api/removeAdminAccess';
import { updateAdminRole } from '../api/updateAdminRole';
import { downloadAdminUsersCsv } from '../helpers/downloadAdminUsersCsv';
import { shouldNotifyAdminUsersError } from '../helpers/shouldNotifyAdminUsersError';
import { updateAdminUserRoles } from '../helpers/updateAdminUserRoles';

type TEditPermissionVariables = {
  uid: string;
  role: string;
  value: boolean;
};

type TPreviousPermissionValue = [QueryKey, boolean];

type TEditPermissionContext = {
  previousPermissionValues: TPreviousPermissionValue[];
};

const getPermissionKey = (uid: string, role: string) => JSON.stringify([uid, role]);
const VS_ADMIN_ROLE = 'ROLE_VS_ADMIN';
const SEARCH_DEBOUNCE_MS = 300;

type TState = {
  users: AdminUsersResponseDto;
  isLoading: boolean;
  hasInitialError: boolean;
  retryUsers: () => void;
  sortingReverse: boolean;
  toggleSort: (field: string) => void;
  search: string;
  setSearch: (v: string) => void;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (v: number) => void;
  editId: string;
  setEditId: (v: string) => void;
  sortingField: string;
  exportCSV: () => void;
  isExporting: boolean;
  editPermission: (uid: string, role: string, value: boolean) => void;
  isPermissionPending: (uid: string, role: string) => boolean;
  deleteUser: (uid: string) => void;
  deletingUserId: string | null;
};

const PageContext = createContext<TState>({
  users: { data: [], count: 0 },
  isLoading: false,
  hasInitialError: false,
  retryUsers: () => null,
  sortingReverse: true,
  toggleSort: () => null,
  search: '',
  setSearch: () => null,
  page: 1,
  pageSize: 25,
  setPage: () => null,
  setPageSize: () => null,
  editId: '',
  setEditId: () => null,
  sortingField: 'createdAt',
  exportCSV: () => {},
  isExporting: false,
  editPermission: () => {},
  isPermissionPending: () => false,
  deleteUser: () => {},
  deletingUserId: null,
});

type TProps = {
  children: ReactNode;
};

export const PageProvider = ({ children }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { page, pageSize, search, orderBy, orderType, setSearch, setPageSize, setPage, toggleSort } =
    useListQueryParams({
      defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
      replaceSearch: true,
    });
  const sortingField = orderBy;
  const sortingReverse = orderType === 'desc';
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const [editId, setEditId] = useState('');
  const [pendingPermissionKeys, setPendingPermissionKeys] = useState<string[]>([]);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const pendingPermissionKeysRef = useRef(new Set<string>());
  const deletingUserIdRef = useRef<string | null>(null);
  const lastNotifiedErrorUpdatedAt = useRef(0);
  const listParams = useMemo<TAdminUsersListParams>(
    () => ({ page, pageSize, orderBy, orderType, search: debouncedSearch }),
    [debouncedSearch, orderBy, orderType, page, pageSize],
  );
  const usersQuery = useQuery({
    queryKey: adminUsersKeys.list(listParams),
    queryFn: createFetchAdminUsersQueryFn(call),
    placeholderData: keepPreviousData,
    enabled: !isSearchSettling,
  });

  useEffect(() => {
    if (
      !usersQuery.isRefetchError ||
      usersQuery.data === undefined ||
      usersQuery.errorUpdatedAt === lastNotifiedErrorUpdatedAt.current
    ) {
      return;
    }
    lastNotifiedErrorUpdatedAt.current = usersQuery.errorUpdatedAt;
    if (shouldNotifyAdminUsersError(usersQuery.error)) {
      toast.error('Unable to refresh admins. Showing previously loaded data.');
    }
  }, [usersQuery.data, usersQuery.error, usersQuery.errorUpdatedAt, usersQuery.isRefetchError]);

  const addPendingPermission = (key: string) => {
    pendingPermissionKeysRef.current.add(key);
    setPendingPermissionKeys([...pendingPermissionKeysRef.current]);
  };

  const removePendingPermission = (key: string) => {
    pendingPermissionKeysRef.current.delete(key);
    setPendingPermissionKeys([...pendingPermissionKeysRef.current]);
  };

  const editPermissionMutation = useMutation<
    AdminToggleAdminRoleResponseDto,
    unknown,
    TEditPermissionVariables,
    TEditPermissionContext
  >({
    mutationFn: ({ uid, role, value }: { uid: string; role: string; value: boolean }) =>
      updateAdminRole({ call, uid, role, value }),
    onMutate: async ({ uid, role, value }) => {
      await queryClient.cancelQueries({ queryKey: adminUsersKeys.lists() });

      const previousPermissionValues: TPreviousPermissionValue[] = [];
      queryClient
        .getQueriesData<AdminUsersResponseDto>({
          queryKey: adminUsersKeys.lists(),
        })
        .forEach(([queryKey, users]) => {
          const admin = users?.data.find((user) => user.id === uid);
          if (admin) {
            previousPermissionValues.push([queryKey, admin.roles.includes(role)]);
          }
        });

      queryClient.setQueriesData<AdminUsersResponseDto>({ queryKey: adminUsersKeys.lists() }, (users) => {
        if (users === undefined) {
          return users;
        }

        return updateAdminUserRoles({ users, uid, role, value });
      });

      return { previousPermissionValues };
    },
    onError: (error, { uid, role }, context) => {
      context?.previousPermissionValues.forEach(([queryKey, previousValue]) => {
        queryClient.setQueryData<AdminUsersResponseDto>(queryKey, (users) => {
          if (users === undefined) {
            return users;
          }

          return updateAdminUserRoles({ users, uid, role, value: previousValue });
        });
      });

      if (shouldNotifyAdminUsersError(error)) {
        toast.error('Unable to update admin permissions.');
      }
    },
    onSettled: async (_data, _error, { uid, role }) => {
      removePendingPermission(getPermissionKey(uid, role));

      if (pendingPermissionKeysRef.current.size > 0) {
        return;
      }

      await queryClient.invalidateQueries({ queryKey: adminUsersKeys.lists() });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: ({ uid }: { uid: string; adminName: string }) => removeAdminAccess({ call, uid }),
    onError: (error) => {
      if (shouldNotifyAdminUsersError(error)) {
        toast.error(error instanceof AdminRemovalError ? error.message : 'Unable to remove admin.');
      }
    },
    onSettled: async (_data, error, { uid, adminName }) => {
      try {
        await queryClient.invalidateQueries({ queryKey: adminUsersKeys.lists() });
        if (error === null) {
          toast.success(`${adminName} has been removed from admin access.`);
        }
      } finally {
        if (deletingUserIdRef.current === uid) {
          deletingUserIdRef.current = null;
        }
        setDeletingUserId((current) => (current === uid ? null : current));
      }
    },
  });

  const exportMutation = useMutation({
    mutationFn: () => fetchAllAdminUsersForExport(call),
    onSuccess: ({ data }) => {
      void downloadAdminUsersCsv(data);
    },
    onError: (error) => {
      if (shouldNotifyAdminUsersError(error)) {
        toast.error('Unable to export admins.');
      }
    },
  });

  const users = usersQuery.data ?? { data: [], count: 0 };
  const isLoading =
    usersQuery.isFetching || isSearchSettling || pendingPermissionKeys.length > 0 || deleteUserMutation.isPending;
  const hasInitialError = usersQuery.isError && usersQuery.data === undefined;
  const retryUsers = () => {
    void usersQuery.refetch();
  };

  const exportCSV = () => {
    if (!exportMutation.isPending) {
      exportMutation.mutate();
    }
  };

  const editPermission = (uid: string, role: string, value: boolean) => {
    if (role === VS_ADMIN_ROLE && !value) {
      return;
    }

    const permissionKey = getPermissionKey(uid, role);
    if (deletingUserIdRef.current === uid || pendingPermissionKeysRef.current.has(permissionKey)) {
      return;
    }

    addPendingPermission(permissionKey);
    editPermissionMutation.mutate({ uid, role, value });
  };

  const deleteUser = (uid: string) => {
    if (
      deleteUserMutation.isPending ||
      deletingUserIdRef.current !== null ||
      pendingPermissionKeysRef.current.size > 0
    ) {
      return;
    }

    const admin = users.data.find((user) => user.id === uid);
    const adminName = admin ? [admin.name, admin.lastname].filter(Boolean).join(' ') || 'Admin' : 'Admin';
    deletingUserIdRef.current = uid;
    setDeletingUserId(uid);
    deleteUserMutation.mutate({ uid, adminName });
  };

  return (
    <PageContext.Provider
      value={{
        users,
        isLoading,
        hasInitialError,
        retryUsers,
        sortingReverse,
        toggleSort,
        search,
        setSearch,
        page,
        setPage,
        pageSize,
        setPageSize,
        editId,
        setEditId,
        sortingField,
        exportCSV,
        isExporting: exportMutation.isPending,
        editPermission,
        isPermissionPending: (uid, role) => pendingPermissionKeysRef.current.has(getPermissionKey(uid, role)),
        deleteUser,
        deletingUserId,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePageContext = () => useContext(PageContext);
